import {
  Box,
  Button,
  Container,
  Divider,
  List,
  ListItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Moment } from "moment";
import { Fragment, useEffect, useState } from "react";
import { useUsuario } from "../../../../providers/usuarioProvider";
import InteiroText from "../../../util/ui/inputs/inteiroText";
import MoedaText from "../../../util/ui/inputs/moedaText";
import Ticker from "../../../util/ui/ticker";
import MoedaInput from "../../../util/ui/inputs/moedaInput";
import InteiroInput from "../../../util/ui/inputs/inteiroInput";
import { IconPlus, IconX } from "@tabler/icons-react";
import ExercicioDireito from "../../../../models/exercicioDireito";
import { map } from "lodash";
import Direito from "../../../../models/direito";
import Functions from "../../../../callable/functions";
import {
  addDoc,
  deleteDoc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import ExercicioDireitoDb from "../../../../db/exercicioDireitoDb";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { radius } from "../../../../theme";

export default function Subscricao() {
  const { visualizado } = useUsuario();
  const [exercicios, setExercicios] = useState<ExercicioDireito[]>();

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new ExercicioDireitoDb().collection({
            usuarioId: visualizado,
            exercicioDireitoId: "",
          }),
          orderBy("dataExercicio", "desc")
        ),
        (query) => setExercicios(map(query.docs, (doc) => doc.data()))
      );
  }, [visualizado]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="eyebrow" color="text.secondary">
        Arquivo
      </Typography>
      <Typography variant="h1" color="text.primary" pt={0.5}>
        Exercícios de Subscrição
      </Typography>
      <Box paddingBottom={4} />
      <Paper variant="outlined" sx={{ borderRadius: `${radius.xl}px` }}>
        <NovoExercicio />
      </Paper>
      <Box paddingBottom={4} />
      <Paper
        variant="outlined"
        sx={{ borderRadius: `${radius.xl}px`, overflow: "hidden" }}
      >
        <List disablePadding>
          {map(exercicios, (exercicio, index) => (
            <Fragment key={exercicio.id}>
              {index > 0 && <Divider variant="fullWidth" />}
              <ExercicioDireitoLisItem exercicio={exercicio} />
            </Fragment>
          ))}
        </List>
      </Paper>
    </Container>
  );
}
function NovoExercicio() {
  const { visualizado } = useUsuario();
  const [codigoAtivo, setCodigoAtivo] = useState<string | undefined>(undefined);
  const [exercicio, setExercicio] = useState<Moment | undefined>(undefined);
  const [quantidade, setQuantidade] = useState<number | undefined>(undefined);
  const [direitoExercido, setDireitoExercido] = useState<Direito | undefined>(
    undefined
  );

  useEffect(() => {
    if (!!codigoAtivo && !!exercicio && exercicio.isValid()) {
      new Functions()
        .direitoAutoComplete({ codigoAtivo, data: exercicio.format() })
        .then((res) => {
          setDireitoExercido(
            res.data?.direito ? Direito.fromJson(res.data.direito) : undefined
          );
        });
    }
  }, [codigoAtivo, exercicio]);

  const criarExercicio = async () => {
    if (
      !!visualizado &&
      !!codigoAtivo &&
      !!exercicio &&
      !!quantidade &&
      !!direitoExercido
    )
      return await addDoc(
        new ExercicioDireitoDb().collection({
          usuarioId: visualizado,
          exercicioDireitoId: "",
        }),
        new ExercicioDireito({
          id: "",
          isinDireito: direitoExercido.isin,
          codigoAtivo: codigoAtivo,
          dataExercicio: exercicio,
          quantidade: quantidade,
          precoUnitario: direitoExercido.precoUnitario,
          codigoObjeto: direitoExercido.codigoObjeto,
        })
      ).then(() => {
        setCodigoAtivo(undefined);
        setExercicio(undefined);
        setQuantidade(undefined);
        setDireitoExercido(undefined);
      });
  };

  return (
    <Box
      paddingX={4}
      paddingY={2}
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      flexWrap="wrap"
    >
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <Typography variant="labelMono" color="text.secondary">
          Código do Direito
        </Typography>
        <TextField
          id="standard-codigoAtivo"
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
          sx={(theme) => ({
            width: "14ch",
            // Ticker é código: mono tabular.
            "& .MuiInputBase-input": {
              fontFamily: theme.typography.dataMono.fontFamily,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.04em",
            },
          })}
          variant="outlined"
          value={codigoAtivo ?? ""}
          onChange={(e) => setCodigoAtivo(e.target.value.toUpperCase())}
          placeholder="CVCB1"
        />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <Typography variant="labelMono" color="text.secondary">
          Exercício
        </Typography>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            sx={(theme) => ({
              width: "18ch",
              // Data é número: mono tabular.
              "& .MuiInputBase-input": {
                fontFamily: theme.typography.dataMono.fontFamily,
                fontVariantNumeric: "tabular-nums",
              },
            })}
            slotProps={{ textField: { size: "small" } }}
            value={exercicio ?? null}
            onChange={(e) => {
              if (e?.isValid()) setExercicio(e.startOf("day"));
            }}
          />
        </LocalizationProvider>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <Typography variant="labelMono" color="text.secondary">
          Quantidade
        </Typography>
        <TextField
          id="standard-quantidade"
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: "10ch" }}
          variant="outlined"
          value={quantidade ?? ""}
          onChange={(e) =>
            // @ts-ignore
            setQuantidade(e.target.floatValue)
          }
          InputProps={{
            inputComponent: InteiroInput as any,
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <Typography variant="labelMono" color="text.secondary">
          Preço Unitário
        </Typography>
        <TextField
          id="standard-precoUnitario"
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: "14ch" }}
          variant="outlined"
          disabled
          value={direitoExercido?.precoUnitario ?? ""}
          InputProps={{
            inputComponent: MoedaInput as any,
          }}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        alignSelf="center"
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ p: 1, minWidth: 0, borderRadius: radius.round }}
          onClick={criarExercicio}
          disabled={
            !visualizado ||
            !codigoAtivo ||
            !exercicio ||
            !quantidade ||
            !direitoExercido
          }
        >
          <IconPlus size={14} />
        </Button>
      </Box>
    </Box>
  );
}

type ExercicioProps = {
  exercicio: ExercicioDireito;
};

function ExercicioDireitoLisItem({ exercicio }: ExercicioProps) {
  const { visualizado } = useUsuario();

  const apagarExercicio = async () => {
    if (!!visualizado)
      return await deleteDoc(
        new ExercicioDireitoDb().doc({
          usuarioId: visualizado,
          exercicioDireitoId: exercicio.id,
        })
      );
  };

  return (
    <ListItem disableGutters>
      <Box
        paddingX={4}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        flexWrap="wrap"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{ width: "14ch" }}
        >
          <Ticker
            codigoAtivo={exercicio.codigoAtivo}
            size="medium"
            color="primary"
          />
          <Box paddingLeft={1} />
          <Ticker codigoAtivo={exercicio.codigoObjeto} />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          sx={{ width: "18ch" }}
        >
          <Typography variant="labelMono" color="text.secondary">
            Exercício
          </Typography>
          <Typography variant="dataMono" color="text.primary">
            {exercicio.dataExercicio.format("DD/MM/YYYY")}
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          sx={{ width: "10ch" }}
        >
          <Typography variant="labelMono" color="text.secondary">
            Quantidade
          </Typography>
          <Typography variant="dataMono" color="text.primary">
            <InteiroText value={exercicio.quantidade} />
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          sx={{ width: "14ch" }}
        >
          <Typography variant="labelMono" color="text.secondary">
            Preço Unitário
          </Typography>
          <Typography variant="dataMono" color="text.primary">
            <MoedaText value={exercicio.precoUnitario} />
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          alignSelf="center"
        >
          <Button
            variant="contained"
            color="error"
            size="small"
            sx={{ p: 1, minWidth: 0, borderRadius: radius.round }}
            onClick={apagarExercicio}
          >
            <IconX size={14} />
          </Button>
        </Box>
      </Box>
    </ListItem>
  );
}
